import { v4 as uuid } from 'uuid';

import { RPage, RPageInput } from '@/store/v2';
import { Page, PageVariantDeprecated as PageVariant, PageVariant as PageVariantV2 } from '@/features/course/admin/types/page';
import {
  createBlock,
  transformBlockFromRemote,
  transformBlockToRemoteInput,
} from '@/administration/pages/Course/store/block/transform';
import { BlockType } from '@/administration/pages/Course/store/block/types';
import { PageId } from '@/administration/pages/Course/store/types';
import { Course } from '@/administration/pages/Course/store/course/types';
import { createResultBlock, createSurveyResultBlock } from '@/administration/pages/Course/store/block/transformResult';
import { SurveyVariant } from '@/features/course/learner/types/survey';
import { PracticalAssessment } from '@/features/course/learner/types/practicalAssessment';
import { ActivityType } from '@/features/course/admin/types/activity';

export const RESULTS_PAGE_ID = `page-l-result`;

export const transformPageTypeToActivityType = (type: PageVariantV2): ActivityType => {
  switch (type) {
    case PageVariantV2.RESULT:
      return ActivityType.Result;
    case PageVariantV2.PRACTICAL_ASSESSMENT_TEXT:
      return ActivityType.PracticalAssessmentText;
    case PageVariantV2.PRACTICAL_ASSESSMENT_DOCUMENT:
      return ActivityType.PracticalAssessmentDocument;
    case PageVariantV2.REFLECTION:
      return ActivityType.Reflection;
    case PageVariantV2.QUIZ:
      return ActivityType.Quiz;
    case PageVariantV2.ASSIGNMENT:
      return ActivityType.Assignment;
    case PageVariantV2.QUESTION:
      return ActivityType.Question;
    case PageVariantV2.MODULE:
      return ActivityType.Module;
    case PageVariantV2.TEXT:
    default:
      return ActivityType.Page;
  }
};

export const transformActivityTypeToPageType = (type: ActivityType): PageVariantV2 => {
  switch (type) {
    case ActivityType.Result:
      return PageVariantV2.RESULT;
    case ActivityType.PracticalAssessmentText:
      return PageVariantV2.PRACTICAL_ASSESSMENT_TEXT;
    case ActivityType.PracticalAssessmentDocument:
      return PageVariantV2.PRACTICAL_ASSESSMENT_DOCUMENT;
    case ActivityType.Reflection:
      return PageVariantV2.REFLECTION;
    case ActivityType.Quiz:
      return PageVariantV2.QUIZ;
    case ActivityType.Assignment:
      return PageVariantV2.ASSIGNMENT;
    case ActivityType.Question:
      return PageVariantV2.QUESTION;
    case ActivityType.Module:
      return PageVariantV2.MODULE;
    case ActivityType.Page:
    default:
      return PageVariantV2.TEXT;
  }
};

const transformPageTypeToBlockType = (type: PageVariant): BlockType => {
  switch (type) {
    case PageVariant.RESULT:
      return BlockType.RESULT;
    case PageVariant.PRACTICAL_ASSESSMENT_TEXT:
      return BlockType.PRACTICAL_ASSESSMENT_TEXT;
    case PageVariant.PRACTICAL_ASSESSMENT_DOCUMENT:
      return BlockType.PRACTICAL_ASSESSMENT_DOCUMENT;
    case PageVariant.PRACTICAL_ASSESSMENT_VIDEO:
      return BlockType.PRACTICAL_ASSESSMENT_VIDEO;
    case PageVariant.REFLECTION:
      return BlockType.REFLECTION;
    case PageVariant.TEXT:
    default:
      return BlockType.TEXT;
  }
};

const transformBlockTypeToPageType = (blockType: BlockType): PageVariant => {
  switch (blockType) {
    case BlockType.QUESTION:
      return PageVariant.QUESTION;
    case BlockType.PRACTICAL_ASSESSMENT_TEXT:
      return PageVariant.PRACTICAL_ASSESSMENT_TEXT;
    case BlockType.PRACTICAL_ASSESSMENT_DOCUMENT:
      return PageVariant.PRACTICAL_ASSESSMENT_DOCUMENT;
    case BlockType.PRACTICAL_ASSESSMENT_VIDEO:
      return PageVariant.PRACTICAL_ASSESSMENT_VIDEO;
    case BlockType.REFLECTION:
      return PageVariant.REFLECTION;
    case BlockType.TEXT:
    default:
      return PageVariant.TEXT;
  }
};

export const createPage = (title: string, type: PageVariant): Page => {
  const page = {
    id: `page-l-${uuid()}` as PageId,
    type,
    title,
    block: createBlock(transformPageTypeToBlockType(type)),
    active: false,
    hasError: false,
  };

  return {
    ...page,
    hasError: validatePage(page),
  };
};

export const createResultPage = (ops: Either<{ course?: Course | PracticalAssessment }, { variant: SurveyVariant }>): Page => {
  return {
    id: RESULTS_PAGE_ID as PageId,
    type: PageVariant.RESULT,
    title: 'Results',
    block: ops.variant ? createSurveyResultBlock(ops.variant) : createResultBlock(ops.course),
    active: false,
    hasError: false,
  };
};

export const updatePage = (page: Page, update: Partial<Page>): Page => {
  const newPage = <Page>{
    ...page,
    ...update,
    block: {
      ...page.block,
      ...update?.block,
    },
  };
  const hasErrors = validatePage(newPage);

  return {
    ...newPage,
    hasError: hasErrors,
  };
};

export const transformPageFromRemote = (rPage: RPage): Page => {
  const block = transformBlockFromRemote(rPage?.blocks?.[0] || undefined);

  const page = {
    id: `page-r-${rPage.id}` as PageId,
    remoteId: rPage.id || undefined,
    title: rPage.name || '',
    type: transformBlockTypeToPageType(block.type),
    block,
    active: false,
    hasError: false,
    completed: false, // TODO need completion from api
  };

  return {
    ...page,
    hasError: validatePage(page),
  };
};

export const transformPageToRemote = (page: Page, moduleId?: number, order?: number): RPageInput => {
  return {
    id: page.remoteId,
    moduleId,
    name: page.title,
    order: order || 0,
    blocks: [transformBlockToRemoteInput(page.block, page.remoteId, 0)],
  };
};

const validatePage = (page?: Page): boolean => {
  if (!page) return true;

  if (page.type === PageVariant.TEXT && page.block.type === BlockType.TEXT) {
    return !page.title || !page.block.text?.length;
  }

  return false;
};
